import { DownloadCsvButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { cn } from '@/lib/utils';
import { useGenerateFarmReport } from '@/modules/Report/Report.api';
import { NoPdf } from '@/modules/Report/Report.nopdf';
import { PdfViewer } from '@/modules/Report/Report.pdfviewer';
import { reportStatus, ReportStatusData } from '@/modules/Report/Report.schema';
import { QueryStatus } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

interface FarmReportProps {
  projectId: string;
  farmId: string;
  visibleTab: boolean;
  isCsvDownloadable: boolean;
  data: ReportStatusData | undefined;
  status: QueryStatus;
  refetch: () => void;
  setRefetching: (refetching: boolean) => void;
}

export const FarmReport = ({ projectId, farmId, visibleTab, isCsvDownloadable, data, status, refetch, setRefetching }: FarmReportProps) => {
  const generateReport = useGenerateFarmReport();
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const generatePdf = useCallback(async () => {
    setGeneratingPdf(true);
    try {
      await generateReport.mutateAsync(farmId);
      toast.success('Report sent for generation.');
      setRefetching(true);
      refetch();
    } catch (error) {
      toast.error((error as Error).message || 'Report generation failed.', {
        autoClose: 3000,
      });
      console.error(error);
    }
    setGeneratingPdf(false);
  }, [generateReport, farmId, setRefetching, refetch]);

  if (status === 'error') {
    toast.error('Failed to fetch farm report status');
    return;
  }

  if (!visibleTab) {
    return null;
  }

  if (status === 'pending' || !data) {
    return <Loading />;
  }

  return (
    <div className="mb-12">
      {isCsvDownloadable && (
        <div className={cn('flex flex-row-reverse mb-4')}>
          <DownloadCsvButton projectId={projectId} farmId={farmId} template="farm_csv_samples" />
        </div>
      )}
      {data.status !== reportStatus.COMPLETED && <NoPdf status={data.status} generatePdf={generatePdf} generatingPdf={generatingPdf} />}
      {data.status === reportStatus.COMPLETED && (
        <div className="h-[750px]">
          <PdfViewer visibleTab={true} src={data.url} generatePdf={generatePdf} generatingPdf={generatingPdf} />
        </div>
      )}
    </div>
  );
};
